import { useRef } from "react";
import { cn } from "@/lib/utils";

const BRANDS = [
  { value: "all", label: "All", logoFile: null },
  { value: "toyota", label: "Toyota", logoFile: "toyota" },
  { value: "honda", label: "Honda", logoFile: "honda" },
  { value: "mercedes", label: "Benz", logoFile: "mercedes" },
  { value: "bmw", label: "BMW", logoFile: "bmw" },
  { value: "audi", label: "Audi", logoFile: "audi" },
  { value: "lexus", label: "Lexus", logoFile: "lexus" },
  { value: "porsche", label: "Porsche", logoFile: "porsche" },
  { value: "tesla", label: "Tesla", logoFile: "tesla" },
  { value: "mazda", label: "Mazda", logoFile: "mazda" },
  { value: "nissan", label: "Nissan", logoFile: "nissan" },
  { value: "hyundai", label: "Hyundai", logoFile: "hyundai" },
  { value: "kia", label: "Kia", logoFile: "kia" },
  { value: "ford", label: "Ford", logoFile: "ford" },
  { value: "mitsubishi", label: "Mitsubishi", logoFile: "mitsubishi" },
  { value: "isuzu", label: "Isuzu", logoFile: "isuzu" },
  { value: "volkswagen", label: "VW", logoFile: "volkswagen" },
  { value: "mg", label: "MG", logoFile: "mg" },
  { value: "byd", label: "BYD", logoFile: "byd" },
  { value: "gwm", label: "GWM", logoFile: "gwm" },
  { value: "subaru", label: "Subaru", logoFile: "subaru" },
  { value: "suzuki", label: "Suzuki", logoFile: "suzuki" },
  { value: "chevrolet", label: "Chevrolet", logoFile: "chevrolet" },
  { value: "peugeot", label: "Peugeot", logoFile: "peugeot" },
  { value: "mini", label: "Mini", logoFile: "mini" },
  { value: "volvo", label: "Volvo", logoFile: "volvo" },
];

interface BrandScrollerProps {
  selectedBrand: string;
  onBrandChange: (brand: string) => void;
  brandCounts: Record<string, number>;
  merged?: boolean;
}

export const BrandScroller = ({ selectedBrand, onBrandChange, brandCounts, merged = false }: BrandScrollerProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const totalCount = Object.values(brandCounts).reduce((sum, c) => sum + c, 0);

  const visibleBrands = BRANDS.filter(
    (b) => b.value === "all" || (brandCounts[b.value] ?? 0) > 0
  );

  const allBrand = visibleBrands.find(b => b.value === "all")!;
  const otherBrands = visibleBrands.filter(b => b.value !== "all");

  const renderBrandBtn = (brand: typeof BRANDS[number]) => {
    const isActive = selectedBrand === brand.value;
    const count = brand.value === "all" ? totalCount : (brandCounts[brand.value] ?? 0);
    return (
      <button
        key={brand.value}
        onClick={() => onBrandChange(brand.value)}
        className={cn(
          "flex items-center gap-2 h-10 px-4 rounded-full border text-sm font-semibold tracking-tight whitespace-nowrap transition-all shrink-0",
          isActive
            ? "border-border/50 shadow-sm"
            : "bg-card text-muted-foreground border-border/50 hover:bg-muted hover:text-foreground"
        )}
        style={isActive ? {
          background: 'linear-gradient(135deg, #e8e8e8 0%, #b0b0b0 25%, #d4d4d4 50%, #a0a0a0 75%, #c8c8c8 100%)',
          color: '#1a1a2e'
        } : undefined}
      >
        {brand.logoFile && (
          <img
            src={`/logos/${brand.logoFile}.png?v=3`}
            alt={brand.label}
            className="h-5 w-5 object-contain"
            loading="lazy"
            decoding="async"
            width={20}
            height={20}
          />
        )}
        {brand.label}
        <span className={cn(
          "text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center",
          isActive ? "bg-black/15 text-[#1a1a2e]" : "bg-muted text-muted-foreground"
        )}>
          {count}
        </span>
      </button>
    );
  };

  return (
    <div className="w-full overflow-hidden">
      <div className={cn("flex items-center gap-2 py-2", merged ? "pl-6 pr-3 sm:pr-4" : "pl-4 pr-2")}>
        {renderBrandBtn(allBrand)}
        <div
          ref={scrollRef}
          className="flex-1 min-w-0 flex gap-2 overflow-x-auto scrollbar-hide"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          {otherBrands.map(renderBrandBtn)}
        </div>
      </div>
    </div>
  );
};
